import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import {
  Api,
  EmployeeCacheKeys,
  PunchAddRequestV2,
  PunchCacheKeys,
  PunchCommentDeleteRequestV2,
  PunchCommentUpdateRequestV2,
  PunchDeleteRequestV2,
  PunchDetailedAddRequestV2,
  PunchDetailedUpdateRequestV2,
  PunchUpdateRequestV2,
  PunchV2ApiApiV2PunchAddConfigurationGetRequest,
  PunchV2ApiApiV2PunchDetailsGetRequest,
  PunchV2ApiApiV2PunchMultiDeleteRequest,
  PunchV2ApiApiV2PunchMultiPostRequest,
  TimeCardCacheKeys,
  useApi,
} from '../index';
import { useInvalidatePunchCache } from './util/use-invalidate-punch-cache';

export const usePunchDetailsWithMutation = () => {
  const api = useApi();
  return useMutation((body: PunchV2ApiApiV2PunchDetailsGetRequest) =>
    api.punchV2Api.apiV2PunchDetailsGet(body).then((r) => r.data)
  );
};
export type PunchDetailedResponse = ReturnType<
  typeof usePunchDetailsWithMutation
>['data'];

export const useDeletePunchComment = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    (punchCommentDeleteRequestV2: PunchCommentDeleteRequestV2) =>
      api.punchV2Api.apiV2PunchCommentDelete({ punchCommentDeleteRequestV2 }),
    {
      onSuccess: async () => {
        await queryClient
          .invalidateQueries([TimeCardCacheKeys.EMPLOYEE_TIME_CARD])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
          .catch(noop);
      },
    }
  );
};

export const useAddEditPunchComment = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    (punchCommentUpdateRequestV2: PunchCommentUpdateRequestV2) =>
      api.punchV2Api.apiV2PunchCommentPut({ punchCommentUpdateRequestV2 }),
    {
      onSuccess: async () => {
        await queryClient
          .invalidateQueries([TimeCardCacheKeys.EMPLOYEE_TIME_CARD])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
          .catch(noop);
      },
    }
  );
};

export const useAddPunch = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const updateCache = useInvalidatePunchCache();

  return useMutation(
    (punchAddRequestV2: PunchAddRequestV2) =>
      api.punchV2Api.apiV2PunchPost({ punchAddRequestV2 }),
    {
      onSuccess: async () => {
        await updateCache();
      },
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useAddPunches = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const updateCache = useInvalidatePunchCache();

  return useMutation(
    (requestParameters: PunchV2ApiApiV2PunchMultiPostRequest) =>
      api.punchV2Api.apiV2PunchMultiPost(requestParameters),
    {
      onSuccess: async () => {
        await updateCache();
      },
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useEditPunch = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const updateCache = useInvalidatePunchCache();

  return useMutation(
    (punchUpdateRequestV2: PunchUpdateRequestV2) =>
      api.punchV2Api.apiV2PunchPut({ punchUpdateRequestV2 }),
    {
      onSuccess: async () => {
        await updateCache();
      },
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useDeletePunch = () => {
  const api = useApi();
  const updateCache = useInvalidatePunchCache();

  return useMutation(
    (punchDeleteRequestV2: PunchDeleteRequestV2) =>
      api.punchV2Api.apiV2PunchDelete({ punchDeleteRequestV2 }),
    {
      onSuccess: async () => {
        await updateCache();
      },
    }
  );
};

export const useDeletePunches = () => {
  const api = useApi();
  const updateCache = useInvalidatePunchCache();

  return useMutation(
    (requestParameters: PunchV2ApiApiV2PunchMultiDeleteRequest) =>
      api.punchV2Api.apiV2PunchMultiDelete(requestParameters),
    {
      onSuccess: async () => {
        await updateCache();
      },
    }
  );
};

const addPunchWithDetails =
  (api: Api) => (punchDetailedAddRequestV2: PunchDetailedAddRequestV2) => {
    return api.punchV2Api.apiV2PunchWithDetailsPost({
      punchDetailedAddRequestV2,
    });
  };

export const useAddPunchWithDetails = () => {
  const api = useApi();
  const updateCache = useInvalidatePunchCache();

  return useMutation(addPunchWithDetails(api), {
    onSuccess: async () => {
      await updateCache();
    },
  });
};

export type PunchDetailedAddRequest = PunchDetailedAddRequestV2;

const editPunchWithDetails =
  (api: Api) =>
  async (punchDetailedUpdateRequestV2: PunchDetailedUpdateRequestV2) => {
    return api.punchV2Api.apiV2PunchDetailsPut({
      punchDetailedUpdateRequestV2,
    });
  };

export type PunchDetailedUpdateRequest = PunchDetailedUpdateRequestV2;

export const useEditPunchWithDetails = () => {
  const api = useApi();
  const updateCache = useInvalidatePunchCache();

  return useMutation(editPunchWithDetails(api), {
    onSuccess: async () => {
      await updateCache();
    },
  });
};

export const usePunchAuditGet = (employeeId: number, punchId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [PunchCacheKeys.AUDIT, { employeeId, punchId }],
    queryFn: () =>
      api.punchV2Api.apiV2PunchAuditsGet({
        employeeId,
        punchId,
      }),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useGetPunchConfiguration = (
  body: PunchV2ApiApiV2PunchAddConfigurationGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [PunchCacheKeys.PUNCH_CONFIGURATION, body],
    queryFn: () =>
      api.punchV2Api
        .apiV2PunchAddConfigurationGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(body.employeeId),
  });
};
export type PunchConfigurationResponseType = NonNullable<
  ReturnType<typeof useGetPunchConfiguration>['data']
>;
